/**
 * EdgeDevices — the super-admin global device view (/admin/edge-devices).
 *
 * Every device across every org, shared and private, with an Owner column and
 * Owner filter chip. The tenant-facing view is a different page entirely
 * (pages/Edges.tsx, /edges): tenants see shared infrastructure as location
 * capacity, never as devices. See plans/edges-page-shared-vs-owned.md.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { CentralEdgeDevice } from '@simcheck/shared-types';
import { AppShell } from '@/components/layout/AppShell';
import { edgeDeviceService } from '../../services/edgeDeviceService';
import { EdgeDeviceTable, useTierVisibleDevices } from '@/components/edges/EdgeDeviceTable';
import { RefreshCw } from 'lucide-react';

export default function EdgeDevices() {
  const navigate = useNavigate();
  const { activeOrganizationId } = useAuth();
  const [devices, setDevices] = useState<CentralEdgeDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Org names so the Owner column and filter chip show readable labels.
  const [orgNames, setOrgNames] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'organizations'));
        const m = new Map<string, string>();
        snap.docs.forEach((d) => m.set(d.id, (d.data() as any)?.name || d.id));
        setOrgNames(m);
      } catch (err) {
        console.warn('Failed to load org names for Owner column:', err);
      }
    })();
  }, []);

  const loadDevices = async () => {
    try {
      setLoading(true);
      const deviceList = await edgeDeviceService.getDevices(true);
      setDevices(deviceList);
    } catch (error) {
      console.error('Error loading edge devices:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDevices();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDevices();
    setRefreshing(false);
  };

  const tierVisibleDevices = useTierVisibleDevices(devices);

  const stats = useMemo(
    () => ({
      total: tierVisibleDevices.length,
      online: tierVisibleDevices.filter((d) => d.status === 'online').length,
      offline: tierVisibleDevices.filter((d) => d.status === 'offline').length,
      modems: tierVisibleDevices.reduce((sum, d) => sum + (d.modems?.length || 0), 0),
    }),
    [tierVisibleDevices],
  );

  return (
    <AppShell>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Edge Devices</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage all edge devices across the platform — shared and private
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleRefresh}
              disabled={refreshing || loading}
              className="flex items-center gap-2 px-4 py-2 bg-card border border-border rounded hover:bg-muted transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-card rounded-lg border border-border dark:border-border-subtle p-4">
            <p className="text-sm text-muted-foreground">Total Devices</p>
            <p className="text-2xl font-bold text-foreground">{stats.total}</p>
          </div>
          <div className="bg-card rounded-lg border border-border dark:border-border-subtle p-4">
            <p className="text-sm text-muted-foreground">Online</p>
            <p className="text-2xl font-bold text-status-online">{stats.online}</p>
          </div>
          <div className="bg-card rounded-lg border border-border dark:border-border-subtle p-4">
            <p className="text-sm text-muted-foreground">Offline</p>
            <p className="text-2xl font-bold text-status-error">{stats.offline}</p>
          </div>
          <div className="bg-card rounded-lg border border-border dark:border-border-subtle p-4">
            <p className="text-sm text-muted-foreground">Total Modems</p>
            <p className="text-2xl font-bold text-foreground">{stats.modems}</p>
          </div>
        </div>

        <EdgeDeviceTable
          devices={devices}
          loading={loading}
          showOwnerColumn
          orgNames={orgNames}
          activeOrganizationId={activeOrganizationId}
          onRowClick={(device) => navigate(`/admin/edge-devices/${device.id}`)}
        />
      </div>
    </AppShell>
  );
}
